import { Box, FormControl, MenuItem, Select, Typography } from '@mui/material'
import { RouteProps } from 'routes/AppRouter'
import { useTranslation } from 'react-i18next'
import React, { ChangeEvent, useEffect, useState } from 'react'
import styles from './ProfileTab.module.scss'
import { Button } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { emptyUserDTO, UserDTO } from 'modules/users/models/User'
import AvatarProfile from 'components/avatar-profile/AvatarProfile'
import { Input, InputTextDescription } from 'features/resource/userProfileStylesMui'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { ROUTE_PATIENTS, ROUTE_USERS_CREATE } from 'routes/routes-constants'
import { getUserContainer } from 'container/user-module'
import { IUserService, USER_SERVICE_KEY } from 'modules/users'
import { getAppContainer, STATUS_SERVICE_KEY } from 'container/app'
import { IStatusService } from 'common/status/StatusService'
import { PATIENT_ROLE_ID } from 'features/users-new/constants'
import dayjs from 'dayjs'
import { CalendarIcon } from 'features/calendar/Calendar'

interface ProfileTabProps extends RouteProps {
  patient?: UserDTO
}

const userService = getUserContainer().get<IUserService>(USER_SERVICE_KEY)
const statusService = getAppContainer().get<IStatusService>(STATUS_SERVICE_KEY)

export const ProfileTab = ({ patient, id }: ProfileTabProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [newPatient, setNewPatient] = useState<UserDTO>(emptyUserDTO())
  const [doctors, setDoctors] = useState<UserDTO[]>([])

  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false)
  const [DateTimePickerError, setDateTimePickerError] = useState<string>('')
  const [dietError, setDietError] = useState<string>('')
  const [doctorError, setDoctorError] = useState<string>('')

  const dietOptions = [
    { value: 'Líquida', label: t('liquidDiet') },
    { value: 'Semilíquida', label: t('semiLiquidDiet') },
    { value: 'Blanda', label: t('softDiet') },
    { value: 'Astringente', label: t('astringentDiet') },
    { value: 'Absoluta', label: t('absoluteDiet') },
  ]

  useEffect(() => {
    const loadDoctors = async () => {
      try {
        const doctorsData = await userService.getUsersByRole('med').toPromise()
        if (doctorsData) {
          setDoctors(doctorsData.items)
        }
      } catch (error) {
        console.error('Error loading doctors:', error)
      }
    }

    loadDoctors()
  }, [])

  useEffect(() => {
    if (patient) {
      setNewPatient(patient)
    }
  }, [patient])

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    setDietError('')
    setDoctorError('')

    let hasError = false

    if (!newPatient.diet) {
      setDietError(t('dietRequired'))
      hasError = true
    }

    if (!newPatient.assignedID) {
      setDoctorError('El médico asignado es requerido')
      hasError = true
    }

    if (hasError) {
      return
    }

    if (!id) {
      userService.add(newPatient).subscribe((data) => {
        if (!data) return
        statusService.sendStatus({ variant: 'success' })
        navigate(`${ROUTE_USERS_CREATE}/${data.id}`)
      })
    } else {
      userService.update(newPatient).subscribe(() => {
        statusService.sendStatus({ variant: 'success' })
      })
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target
    const updatedPatient = {
      ...newPatient,
      [id]: value,
      rolesIds: [PATIENT_ROLE_ID],
      isPatient: false,
    }
    setNewPatient(updatedPatient)
  }

  const handleDateChange = (date: any) => {
    const updatedPatient = {
      ...newPatient,
      dobv: date.toISOString(),
      rolesIds: [PATIENT_ROLE_ID],
      isPatient: false,
    }
    setNewPatient(updatedPatient)
  }

  const handleDietChange = (event: any) => {
    const updatedPatient = {
      ...newPatient,
      diet: event.target.value,
      rolesIds: [PATIENT_ROLE_ID],
      isPatient: false,
    }
    setNewPatient(updatedPatient)
  }

  const handleDoctorChange = (event: any) => {
    const doctorId = event.target.value
    const updatedPatient = {
      ...newPatient,
      assignedID: doctorId,
    }
    setNewPatient(updatedPatient)

    if (doctorId) {
      setDoctorError('')
    }
  }

  return (
    <form className={styles.form} id={'editUser'}>
      <Box width={'100%'} display={'flex'} bgcolor="transparent" m="1rem">
        <Box id={'Seccion Izquierda'} className={styles.avatarContainer}>
          <AvatarProfile />
          <Box maxWidth={'100%'}>
            <Typography className={styles.userName}>
              {`${newPatient.firstName} ${newPatient.lastName}`}
            </Typography>
            <Typography className={styles.roleText}>{t('resident')}</Typography>
          </Box>
        </Box>
        <Box
          display={'flex'}
          flexGrow={1}
          id={'Seccion Derecha'}
          flexDirection={'column'}
          justifyContent={'space-between'}
        >
          <Box id={'Patient Form'}>
            <Box style={{ maxWidth: '100%' }} className={styles.headerContainer}>
              <Typography className={styles.headerTitle}>{t('userData')}</Typography>
            </Box>

            <Box className={styles.twoColumnContainer}>
              <Box className={styles.fieldColumn}>
                <Box className={styles.fieldContainer}>
                  <Typography className={styles.inputTitle}>{t('name')}</Typography>
                  <Input
                    id={'firstName'}
                    label=""
                    key={'firstName'}
                    name="firstName"
                    className={styles.textField}
                    value={newPatient.firstName}
                    onChange={handleChange}
                    type="text"
                    required={true}
                    variant={'outlined'}
                    size={'small'}
                    fullWidth
                  />
                </Box>

                <Box className={styles.fieldContainer}>
                  <Typography className={styles.inputTitle}>{t('birthDate')}</Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                    <DatePicker
                      sx={{ width: '100%' }}
                      key={'dob'}
                      name="dob"
                      value={patient && patient.dob ? dayjs(patient.dob) : dayjs(new Date())}
                      open={isDatePickerOpen}
                      onOpen={() => setIsDatePickerOpen(true)}
                      onClose={() => setIsDatePickerOpen(false)}
                      format={'DD/MM/YYYY'}
                      onChange={(e) => {
                        handleDateChange(e as unknown as Date)
                      }}
                      label={''}
                      onError={(reason) => {
                        switch (reason) {
                          case 'invalidDate':
                            setDateTimePickerError(t('invalidDateMessage'))
                            break
                          case 'minDate':
                            setDateTimePickerError(t('minDateMessage'))
                            break
                        }
                      }}
                      slotProps={{
                        textField: {
                          id: 'dob',
                          size: 'small',
                          InputProps: {
                            sx: {
                              width: '100%',
                              '& fieldset': {
                                borderRadius: 32,
                                border: '1px solid #E8E7EC',
                                fontFamily: 'Montserrat-Regular',
                              },
                              '&.MuiInputBase-root': { fontFamily: 'Montserrat-Regular' },
                              '&.MuiOutlinedInput-root': {
                                border: 'none',
                                '&.Mui-focused fieldset': {
                                  borderColor: '#2E7161',
                                },
                              },
                            },
                            onClick: () => setIsDatePickerOpen(true),
                          },
                        },
                      }}
                      slots={{
                        openPickerIcon: CalendarIcon,
                      }}
                    />
                  </LocalizationProvider>
                  {DateTimePickerError && (
                    <Typography className={styles.errorText}>{DateTimePickerError}</Typography>
                  )}
                </Box>

                <Box className={styles.fieldContainer}>
                  <Typography className={styles.inputTitle}>{t('diet')}</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      id="diet"
                      value={newPatient.diet || ''}
                      onChange={handleDietChange}
                      displayEmpty
                      required
                      sx={{
                        borderRadius: '32px',
                        border: '1px solid #E8E7EC',
                        fontFamily: 'Montserrat-Regular',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#2E7161',
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        {t('selectDiet')}
                      </MenuItem>
                      {dietOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {dietError && <Typography className={styles.errorText}>{dietError}</Typography>}
                  </FormControl>
                </Box>

                <Box className={styles.fieldContainer}>
                  <Typography className={styles.inputTitle}>{t('diagnosisSummary')}</Typography>
                  <Input
                    id={'diagnosisSummary'}
                    label={''}
                    key={'diagnosisSummary'}
                    name="diagnosisSummary"
                    className={styles.textField}
                    value={newPatient.diagnosisSummary}
                    onChange={handleChange}
                    type="text"
                    required={true}
                    variant={'outlined'}
                    size={'small'}
                    fullWidth
                  />
                </Box>
              </Box>

              <Box className={styles.fieldColumn}>
                <Box className={styles.fieldContainer}>
                  <Typography className={styles.inputTitle}>{t('lastName')}</Typography>
                  <Input
                    id={'lastName'}
                    label={''}
                    key={'lastName'}
                    name="lastName"
                    className={styles.textField}
                    value={newPatient.lastName}
                    onChange={handleChange}
                    type="text"
                    required={true}
                    variant={'outlined'}
                    size={'small'}
                    fullWidth
                  />
                </Box>

                <Box marginRight={'20px'} width={'50%'}>
                  <Typography className={styles.inputTitle}>{t('role')}</Typography>
                  <Typography className={styles.typoText} sx={{ paddingLeft: '14px' }}>
                    {t('resident')}
                  </Typography>
                </Box>

                <Box className={styles.fieldContainer}>
                  <Typography className={styles.inputTitle}>{t('assignedDoctor')}</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={newPatient.assignedID || ''}
                      onChange={handleDoctorChange}
                      displayEmpty
                      required
                      sx={{
                        borderRadius: '32px',
                        border: '1px solid #E8E7EC',
                        fontFamily: 'Montserrat-Regular',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#2E7161',
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        {t('selectDoctor')}
                      </MenuItem>
                      {doctors.map((doctor) => (
                        <MenuItem key={doctor.id} value={doctor.id}>
                          {doctor.firstName} {doctor.lastName}
                        </MenuItem>
                      ))}
                    </Select>
                    {doctorError && (
                      <Typography className={styles.errorText}>{doctorError}</Typography>
                    )}
                  </FormControl>
                </Box>
              </Box>
            </Box>

            <Box className={styles.fullWidthField}>
              <Typography className={styles.inputTitle}>{t('diagnosis')}</Typography>
              <InputTextDescription
                id={'diagnosis'}
                label={''}
                key={'diagnosis'}
                name="diagnosis"
                value={newPatient.diagnosis}
                onChange={handleChange}
                multiline
                rows={4}
                required={true}
                variant={'outlined'}
                fullWidth
              />
            </Box>
          </Box>
          <Box id={'Form y Buttons'} display="flex" justifyContent={'flex-end'} mb={4}>
            <Box
              className={styles.buttonContainer}
              display="flex"
              paddingLeft="40px"
              paddingBottom="5px"
            >
              <Button className={styles.buttonSubmit} type={'button'} onClick={handleSave}>
                {t('save')}
              </Button>
              <Button
                className={styles.buttonCancel}
                type={'button'}
                onClick={() => navigate(ROUTE_PATIENTS)}
              >
                {t('cancel')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </form>
  )
}
